/**
 * 🆕 2026-04-30 — Dialog partagé d'aperçu/envoi du courriel de confirmation de RDV.
 *
 * À utiliser PARTOUT où l'on cédule une estimation pour avoir une UX et un
 * contenu identiques :
 *   - CalendrierRendezVous.jsx (bouton "Céduler" depuis un dossier)
 *   - CreerDossier.jsx (création client + cédulage RDV)
 *
 * Le dialog reproduit fidèlement la maquette validée par le propriétaire :
 *   - Titre "Courriel de confirmation — Aperçu"
 *   - Destinataire éditable
 *   - Sujet éditable
 *   - Tabs Modifier / Aperçu (Aperçu = HTML rendu via /api/crm/rendez-vous/preview-html-email ou template)
 *   - Note ICS + boutons Confirmer/Décliner
 *   - Bouton vert "Céduler et envoyer le courriel" (data-testid=send-email-btn)
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { EmailPreviewFrame } from '@/components/EmailPreview';

export const RdvEmailConfirmationDialog = ({
  open,
  onOpenChange,
  emailData,
  onChange,
  onSend,
  sending = false,
  previewHtml,
  loadingPreview = false,
  fetchPreview, // (contenu: string) => void
  cancelLabel = 'Annuler',
  sendLabel = 'Céduler et envoyer le courriel',
  sendingLabel = 'Envoi...',
}) => {
  const updateField = (field, value) => onChange({ ...emailData, [field]: value });
  const setPreviewMode = (preview) => {
    onChange({ ...emailData, _preview: preview });
    if (preview && fetchPreview) fetchPreview(emailData.contenu || '');
  };
  const isPreview = !!emailData?._preview;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        onPointerDownOutside={(e) => e.preventDefault()}
        data-testid="rdv-email-confirmation-dialog"
      >
        <DialogHeader>
          <DialogTitle>Courriel de confirmation — Aperçu</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Destinataire</Label>
            <Input
              value={emailData?.email || ''}
              onChange={(e) => updateField('email', e.target.value)}
              data-testid="rdv-email-destinataire"
            />
          </div>
          <div>
            <Label>Sujet</Label>
            <Input
              value={emailData?.sujet || ''}
              onChange={(e) => updateField('sujet', e.target.value)}
              data-testid="rdv-email-sujet"
            />
          </div>
          <div>
            <Label>Message</Label>
            <div className="flex gap-0 mt-1">
              <button
                type="button"
                className={`px-3 py-1 rounded-t border text-xs font-medium ${
                  !isPreview ? 'bg-white border-b-white font-semibold' : 'bg-gray-100 text-gray-500'
                }`}
                onClick={() => setPreviewMode(false)}
                data-testid="rdv-email-tab-modifier"
              >
                Modifier
              </button>
              <button
                type="button"
                className={`px-3 py-1 rounded-t border text-xs font-medium ${
                  isPreview ? 'bg-white border-b-white font-semibold' : 'bg-gray-100 text-gray-500'
                }`}
                onClick={() => setPreviewMode(true)}
                data-testid="rdv-email-tab-apercu"
              >
                Aperçu
              </button>
            </div>
            {isPreview ? (
              loadingPreview ? (
                <div
                  className="border rounded-b-md rounded-tr-md p-6 min-h-[200px] flex items-center justify-center bg-gray-50"
                  data-testid="rdv-email-preview-loading"
                >
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                </div>
              ) : (
                <EmailPreviewFrame html={previewHtml} minHeight={300} />
              )
            ) : (
              <Textarea
                value={emailData?.contenu || ''}
                onChange={(e) => updateField('contenu', e.target.value)}
                rows={12}
                className="rounded-t-none"
                data-testid="rdv-email-contenu"
              />
            )}
          </div>
          <p className="text-xs text-gray-500">
            Le client recevra des boutons "Confirmer" / "Décliner" et un fichier .ICS dans le courriel.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="rdv-email-btn-annuler"
            >
              {cancelLabel}
            </Button>
            <Button
              onClick={onSend}
              disabled={sending}
              className="bg-green-600 hover:bg-green-700"
              data-testid="send-email-btn"
            >
              {sending ? sendingLabel : sendLabel}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RdvEmailConfirmationDialog;
